import { api } from '@api'
import { useQuery, useQueries } from '@tanstack/react-query'
import { Contact } from './types/contactTypes'
import { useMemo } from 'react'

interface GetContactByIdResponse {
  contact: Contact
}

const fetchContactById = async (id: string): Promise<Contact> => {
  const { data } = await api.get<GetContactByIdResponse>(`/contact/${id}`)
  return data.contact
}

export const useGetContactById = (id: string) => {
  return useQuery<Contact>({
    queryKey: ['contact', id],
    queryFn: () => fetchContactById(id),
    enabled: !!id,
  })
}

/**
 * Hook to fetch multiple contacts by their IDs in parallel
 * @param ids - Array of contact IDs to fetch
 * @returns Object with contacts array and loading/error states
 */
export const useGetContactsByIds = (ids: string[]) => {
  const uniqueIds = useMemo(() => Array.from(new Set(ids.filter(Boolean))), [ids])
  
  const queries = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['contact', id],
      queryFn: () => fetchContactById(id),
      enabled: !!id,
    })),
  })

  const contacts = useMemo(() => {
    return queries
      .map((query) => query.data)
      .filter((contact): contact is Contact => contact !== undefined)
  }, [queries])

  const isLoading = queries.some((query) => query.isLoading)
  const isError = queries.some((query) => query.isError)
  const error = queries.find((query) => query.error)?.error

  return {
    contacts,
    isLoading,
    isError,
    error,
  }
}

